import { Currency } from "@redotech/money/currencies";
import { TimeUnit } from "@redotech/redo-common-model/time";
import { TriggerProduct } from "@redotech/redo-flows-core-schema-common/type-system/data-types/marketing-types";
import { SchemaFields } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { MOCK_PRODUCT_IMAGE_URL } from "@redotech/redo-marketing-mock-common/mock-values";

/**
 * The products a "product-providing" automation is about. Triggers leave these
 * empty; the send fills them in from `cartContext`, so prices are current.
 */
export enum ProductVariableName {
  CART_PRODUCTS = "cartProducts",
  BROWSED_PRODUCTS = "browsedProducts",
  RESTOCKED_PRODUCT = "restockedProduct",
  DISCOUNTED_PRODUCT = "discountedProduct",
  LOW_STOCK_PRODUCT = "lowStockProduct",
}

export function isProductVariableName(
  name: string,
): name is ProductVariableName {
  return Object.values(ProductVariableName).includes(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    name as ProductVariableName,
  );
}

/** Where a product variable's products come from. */
export type ProductVariableSource =
  /** The abandoned cart the automation triggered on. */
  | { readonly from: "cart" }
  /**
   * Browse abandonment fires on a page view, so the cart is empty and the
   * products come from what the customer actually looked at.
   */
  | {
      readonly from: "recentlyViewed";
      readonly timeframe: { readonly unit: TimeUnit; readonly value: number };
    }
  /** These triggers are about one variant and carry no cart. */
  | { readonly from: "variant"; readonly variantIdField: string };

/**
 * Exhaustive so a new product variable cannot be added without saying where it
 * resolves from; otherwise it would silently render nothing.
 */
export const productVariableSources: Record<
  ProductVariableName,
  ProductVariableSource
> = {
  [ProductVariableName.CART_PRODUCTS]: { from: "cart" },
  [ProductVariableName.BROWSED_PRODUCTS]: {
    from: "recentlyViewed",
    // A browse-abandonment email is about the whole week's browsing, not just
    // the page view that tripped the trigger.
    timeframe: { unit: TimeUnit.DAY, value: 7 },
  },
  [ProductVariableName.RESTOCKED_PRODUCT]: {
    from: "variant",
    variantIdField: "productVariantId",
  },
  [ProductVariableName.DISCOUNTED_PRODUCT]: {
    from: "variant",
    variantIdField: "numericVariantId",
  },
  [ProductVariableName.LOW_STOCK_PRODUCT]: {
    from: "variant",
    variantIdField: "productVariantId",
  },
};

/** Only the variant-sourced triggers are about a single product. */
export const holdsOneProduct = (source: ProductVariableSource) =>
  source.from === "variant";

const listUsage =
  "Loop it with `{% for p in ... %}`, then use `p.title`, `p.url`, `p.image_url`, and `p.price`.";

const singleUsage = "Use `.title`, `.url`, `.image_url`, and `.price` on it.";

export const cartProductsFields = {
  [ProductVariableName.CART_PRODUCTS]: {
    dataType: "Maybe Multiple Trigger Product",
    documentation: `The products in the customer's abandoned cart. ${listUsage}`,
    hideFromConditionStep: true,
  },
} as const satisfies SchemaFields;

export const browsedProductsFields = {
  [ProductVariableName.BROWSED_PRODUCTS]: {
    dataType: "Maybe Multiple Trigger Product",
    documentation: `The products the customer viewed before leaving. ${listUsage}`,
    hideFromConditionStep: true,
  },
} as const satisfies SchemaFields;

export const restockedProductFields = {
  [ProductVariableName.RESTOCKED_PRODUCT]: {
    dataType: "Maybe Trigger Product",
    documentation: `The product that came back in stock. ${singleUsage}`,
    hideFromConditionStep: true,
  },
} as const satisfies SchemaFields;

export const discountedProductFields = {
  [ProductVariableName.DISCOUNTED_PRODUCT]: {
    dataType: "Maybe Trigger Product",
    documentation: `The product whose price dropped. ${singleUsage}`,
    hideFromConditionStep: true,
  },
} as const satisfies SchemaFields;

export const lowStockProductFields = {
  [ProductVariableName.LOW_STOCK_PRODUCT]: {
    dataType: "Maybe Trigger Product",
    documentation: `The product that is running low on stock. ${singleUsage}`,
    hideFromConditionStep: true,
  },
} as const satisfies SchemaFields;

export const exampleTriggerProduct: TriggerProduct = {
  title: "Everyday Merino Crew",
  variantTitle: "Medium / Charcoal",
  url: "https://example.com/products/everyday-merino-crew?variant=222",
  imageUrl: MOCK_PRODUCT_IMAGE_URL,
  price: "$98.00",
  priceAmount: 98,
  compareAtPrice: "$128.00",
  compareAtPriceAmount: 128,
  currencyCode: Currency.USD,
  currencySymbol: "$",
  quantity: 1,
  productId: "111",
  variantId: "222",
};

const secondExampleTriggerProduct: TriggerProduct = {
  title: "Trail Runner Sock",
  variantTitle: null,
  url: "https://example.com/products/trail-runner-sock",
  imageUrl: MOCK_PRODUCT_IMAGE_URL,
  price: "$18.00",
  priceAmount: 18,
  compareAtPrice: null,
  compareAtPriceAmount: null,
  currencyCode: Currency.USD,
  currencySymbol: "$",
  quantity: 2,
  productId: "333",
  variantId: "444",
};

/**
 * Two products, so a `{% for %}` preview shows the loop repeating and that
 * optional fields can be absent.
 */
export const exampleTriggerProducts: TriggerProduct[] = [
  exampleTriggerProduct,
  secondExampleTriggerProduct,
];
